import { useState } from "react";
import Avatar from "@/components/base/Avatar";
import EmptyState from "@/components/base/EmptyState";
import { Skeleton } from "@/components/base/Skeleton";
import { useApi } from "@/hooks/useApi";
import type { GameItem, GameRankResult } from "@/lib/types";
import { formatScore } from "../utils";

interface Props {
  games: GameItem[];
}

// 전체 / 이번 주 토글. week=1 을 붙였다 뗀다.
const PERIODS = [
  { key: "", label: "전체" },
  { key: "&week=1", label: "이번 주" },
];

function medal(rank: number): string | null {
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return null;
}

// 🏆 명예의 전당 — 게임 탭을 눌러 게임별 랭킹을 본다.
// 탭 목록도 games.php 응답에서 만들어 코드에 박지 않는다.
export default function HonorRoll({ games }: Props) {
  const [code, setCode] = useState<string | null>(null);
  const [period, setPeriod] = useState("");

  const active = code ?? games[0]?.code ?? null;
  const path = active
    ? `games.php?game=${encodeURIComponent(active)}${period}`
    : null;
  const { data, loading, error, reload } = useApi<GameRankResult>(path);

  const unit = data?.unit ?? "점";
  const rows = data?.rank ?? [];
  const my = data?.my;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
          <span aria-hidden="true">🏆</span>
          명예의 전당
        </h2>
        {/* 전체 / 이번 주 — 좌우 여백 = 위아래 여백 (p-1) */}
        <div className="inline-flex items-center gap-1 rounded-full bg-background-100 p-1">
          {PERIODS.map((p) => {
            const on = period === p.key;
            return (
              <button
                key={p.key || "all"}
                type="button"
                onClick={() => setPeriod(p.key)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                  on
                    ? "bg-background-50 text-foreground-950"
                    : "text-foreground-600 hover:text-foreground-800"
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 게임 탭 */}
      <div className="mb-5 flex flex-wrap gap-2">
        {games.map((g) => {
          const on = g.code === active;
          return (
            <button
              key={g.code}
              type="button"
              onClick={() => setCode(g.code)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                on
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
              }`}
            >
              <span aria-hidden="true">{g.icon || "🎮"}</span>
              {g.name}
            </button>
          );
        })}
      </div>

      {/* 내 기록 한 줄 */}
      {!loading && !error && my && Number(my.score ?? 0) > 0 && (
        <p className="mb-4 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm text-foreground-700">
          내 최고{" "}
          <strong className="font-bold text-foreground-950">
            {formatScore(my.score)}
            {unit}
          </strong>
          {my.rank ? ` · ${my.rank}위` : ""}
          {data?.total_player ? ` / ${data.total_player}명` : ""}
          {Number(my.play_cnt ?? 0) > 0 ? ` · ${my.play_cnt}회 플레이` : ""}
        </p>
      )}

      {loading ? (
        <div className="space-y-2.5 rounded-xl border border-background-200 bg-background-50 p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      ) : error ? (
        <EmptyState
          icon="ri-error-warning-line"
          title="랭킹을 불러오지 못했습니다"
          description={error}
          action={
            <button
              type="button"
              onClick={reload}
              className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              다시 시도
            </button>
          }
        />
      ) : rows.length === 0 ? (
        <p className="rounded-xl border border-dashed border-background-300 bg-background-50 px-5 py-10 text-center text-sm text-foreground-500">
          아직 기록이 없습니다. 첫 기록의 주인공이 되어보세요!
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-background-200 bg-background-50">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200 bg-background-100 text-xs text-foreground-600">
                <th className="w-16 px-4 py-3 text-left font-semibold">순위</th>
                <th className="px-4 py-3 text-left font-semibold">회원</th>
                <th className="px-4 py-3 text-right font-semibold">기록</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {rows.map((row) => {
                const m = medal(row.rank);
                const mine = row.is_mine === 1;
                return (
                  <tr
                    key={`${row.rank}-${row.name}-${row.date ?? ""}`}
                    className={mine ? "bg-primary-50" : undefined}
                  >
                    <td className="px-4 py-3">
                      {m ? (
                        <span className="text-base" aria-hidden="true">
                          {m}
                        </span>
                      ) : (
                        <span className="text-sm font-bold tabular-nums text-foreground-700">
                          {row.rank}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2.5">
                        <Avatar name={row.name} photo={row.photo} size={32} />
                        <span className="min-w-0">
                          <span className="flex items-center gap-1.5">
                            <span className="truncate text-sm font-semibold text-foreground-900">
                              {row.aho ? `${row.aho} ` : ""}
                              {row.name}
                            </span>
                            {mine && (
                              <span className="shrink-0 rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-semibold text-primary-700">
                                나
                              </span>
                            )}
                          </span>
                          {row.date && (
                            <span className="text-xs text-foreground-400">
                              {row.date}
                            </span>
                          )}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-heading text-sm font-bold tabular-nums text-foreground-950">
                        {formatScore(row.score)}
                      </span>
                      <span className="ml-1 text-xs text-foreground-500">{unit}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}